import React from 'react';
import {IVendor} from "../../models/Search";
import {Callout, DefaultButton, DirectionalHint, Icon, ProgressIndicator, TooltipHost} from "@fluentui/react";
import CurrencyFormat from "react-currency-format";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {useId} from "@fluentui/react-hooks";
import AddToCollectionButton from "../ProductDetails/AddToCollectionButton";
import {WarehouseTable} from "./WarehouseTable";
import PriceBreaksTable from './PriceBreakTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { IRealTimeExtensionResponseItemPriceBreak } from '../../models/Extension';
import CurrencyDisplay from '../Currency/CurrencyDisplay';

interface IVendorDisplayProps {
    refreshing?: boolean;
    loadingWarehouses?: boolean;
    vendor?: IVendor[];
    product: INormalizedProduct;
    dense?: boolean;
    isDemo?: boolean;
}

const VendorDisplay: React.FC<IVendorDisplayProps> = (props) => {
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);
    const [open, setOpen] = React.useState(false);
    const id = useId('vendor-button');

    if (props.vendor == null) return (<div className='no-stock-dense figure-cell'>
        <span className='figure-top' />
        <span className='figure-main' style={{fontWeight: 400}}>No Data</span>
    </div>);

    function formatCurrency(amount: number) {
        if (amount == null) return null;
        return <CurrencyFormat
            value={amount}
            displayType="text"
            thousandSeparator=","
            prefix="$"
            decimalScale={2}
            fixedDecimalScale={true}
        />;
    }

    let stock = false;
    let vendorName;
    let costMessage;
    let hasPriceBreaks = false;
    let highInStockCost: number | undefined;
    let lowInStockCost: number | undefined;
    let highCost: number | undefined;
    let lowCost: number | undefined;
    let lowInStockSingleCost: number | undefined;
    let lowSingleCost: number | undefined;
    let totalStock = 0;
    for (const v of props.vendor) {
        if (v.onHand) totalStock += v.onHand;
        if (v.vendorName) vendorName = v.vendorName;
        if (v.costMessage) costMessage = v.costMessage;
        if (v.inStock) {
            stock = true;
            if (v.cost) {
                if (highInStockCost == null || v.cost > highInStockCost) highInStockCost = v.cost;
                if (lowInStockCost == null || v.cost < lowInStockCost) lowInStockCost = v.cost;
                if (lowInStockSingleCost == null || v.cost < lowInStockSingleCost) lowInStockSingleCost = v.cost;
            }
        }
        if (v.cost) {
            if (highCost == null || v.cost > highCost) highCost = v.cost;
            if (lowCost == null || v.cost < lowCost) lowCost = v.cost;
            if (lowSingleCost == null || v.cost < lowSingleCost) lowSingleCost = v.cost;
        }
        if (v.priceBreaks != null) {
            let pbLow;
            for (const pb of v.priceBreaks) {
                if (pbLow == null || pb.price < pbLow) pbLow = pb.price;
                if (highCost == null || pb.price > highCost) highCost = pb.price;
                if (lowCost == null || pb.price < lowCost) lowCost = pb.price;
                if (v.inStock) {
                    if (highInStockCost == null || pb.price > highInStockCost) highInStockCost = pb.price;
                    if (lowInStockCost == null || pb.price < lowInStockCost) lowInStockCost = pb.price;
                }
            }
            if (v.cost != pbLow) hasPriceBreaks = true;
        }
    }

    const costDisplay = (high: number | undefined, low: number | undefined) => {
        if (high && low) {
            if (high === low) return <span style={{fontWeight: 'bold'}}>{formatCurrency(high)}</span>;
            return <>
                {formatCurrency(low)} - {formatCurrency(high)}
            </>;
        } else if (low) {
            return formatCurrency(low);
        } else if (high) {
            return formatCurrency(high);
        }
        return null;
    }

    const calculateCost = () => {
        if (stock) {
            return costDisplay(highInStockCost, lowInStockCost) ?? costDisplay(highCost, lowCost);
        }
        return costDisplay(highCost, lowCost);
    }

    const cost = calculateCost();

    const inStock = stock;
    // Set by the API when this row came from the selected sub-organization's price file rather than
    // the organization's catalog pricing. Deliberately not read from redux: this renders once per
    // vendor cell per row, so a per-cell store subscription would be a lot of bookkeeping for a label.
    //
    // every, not some: this cell groups every entry for one vendor NAME, and a product can carry more
    // than one part number from the same distributor, of which the customer's price file may cover only
    // some. The cost shown is a range across all of them, so the label is only honest when the whole
    // range is customer priced.
    const customerPricing = props.vendor.length > 0 && props.vendor.every(v => v.subOrganizationPricing);
    const inStockMessage = totalStock && totalStock <= 9999 ? `Stock: ${totalStock}` : 'In Stock';
    const enoughStock = desiredQuantity == null || totalStock != null && totalStock >= desiredQuantity;

    return <div
        className={`vendor-dense ${inStock ? 'in-stock-dense' : 'no-stock-dense'} ${props.refreshing && (inStock ? 'vendor-loading-in-stock-cell' : 'vendor-loading-no-stock-cell')}`}
        >
        <VendorDisplayDataDense
            customerPricing={customerPricing}
            inStock={inStock}
            refreshing={props.refreshing}
            inStockMessage={inStockMessage}
            costMessage={costMessage}
            priceBreaks={props.vendor[0]?.priceBreaks}
            dense={props.dense}
            lowCost={lowCost}
            highCost={highCost}
            lowInStockCost={lowInStockCost}
            highInStockCost={highInStockCost}
            lowInStockSingleCost={lowInStockSingleCost}
            lowSingleCost={lowSingleCost}
            totalStock={totalStock}
            enoughStock={enoughStock}
            vendors={props.vendor}
            product={props.product}
            isDemo={props.isDemo}
            vendorName={vendorName}
        />
    </div>
}

interface IVendorDisplayData {
    inStock?: boolean;
    refreshing?: boolean;
    inStockMessage?: string;
    costMessage?: string;
    priceBreaks?: IRealTimeExtensionResponseItemPriceBreak[];
    dense?: boolean;
    lowCost?: number;
    highCost?: number;
    lowInStockCost?: number;
    highInStockCost?: number;
    lowInStockSingleCost?: number;
    lowSingleCost?: number;
    totalStock?: number;
    enoughStock?: boolean;
    vendors?: IVendor[];
    product: INormalizedProduct;
    isDemo?: boolean;
    vendorName?: string;
    /** Every vendor row is priced from the customer's own price file. */
    customerPricing?: boolean;
}

const VendorDisplayData: React.FC<IVendorDisplayData> = (props) => {
    if (props.lowInStockSingleCost) {
        return <>
            <div style={{fontWeight: 'bold'}}>
                <CurrencyDisplay value={props.lowInStockSingleCost} />
            </div>
        </>
    }
    if (props.lowSingleCost) {
        return <>
            <div style={{fontWeight: 'bold'}}>
                <CurrencyDisplay value={props.lowSingleCost} />
            </div>
        </>
    }
    if (props.highInStockCost != null && props.lowInStockCost && props.lowInStockCost !== props.highInStockCost) {
        return <div>
            <CurrencyDisplay value={props.lowInStockCost!} /> - <CurrencyDisplay value={props.highInStockCost!} />
        </div>
    }
    if (props.highInStockCost != null || props.lowInStockCost != null) {
        return <div style={{fontWeight: 'bold'}}>
            <CurrencyDisplay value={props.highInStockCost ?? props.lowInStockCost} />
        </div>
    }
    if (props.highCost != null && props.lowCost != null && props.highCost !== props.lowCost) {
        return <div>
            <CurrencyDisplay value={props.lowCost} /> - <CurrencyDisplay value={props.highCost} />
        </div>
    }
    if (props.highCost != null || props.lowCost != null) {
        return <div>
            <CurrencyDisplay value={props.highCost ?? props.lowCost} />
        </div>
    }
    return <div style={{fontWeight: 'bold'}}>
        N/A
    </div>
}

const VendorDisplayDataDense: React.FC<IVendorDisplayData> = (props) => {
    const [open, setOpen] = React.useState(false);
    const id = useId('vendor-data-button');

    // Same three-slot layout as the Availability / Cost / Best Price cells, so the vendor's cost
    // sits on the row's figure line: stock on top, cost as the figure, notes beneath.
    const stockLine = (
        <span className='figure-top' style={{color: !props.enoughStock ? 'red' : undefined}}>
            {props.inStock ? props.inStockMessage : 'No Stock'}
        </span>
    );
    const customerPricingTag = props.customerPricing && (
        <span className='customer-pricing-tag' title={"Priced from this customer's own price file"}>Customer pricing</span>
    );
    const hasPriceBreaks = props.priceBreaks != null && props.priceBreaks.some(pb => pb.quantity > 1);

    if (!props.dense || !hasPriceBreaks) {
        return <div
            id={id}
            onClick={() => setOpen(!open)}
            className='figure-cell'
        >
            {stockLine}
            <span className='figure-main'><VendorDisplayData {...props} /></span>
            <span className='figure-bottom'>
                {hasPriceBreaks && <span style={{fontStyle: 'normal'}}>(Price Breaks)</span>}
                {customerPricingTag}
            </span>
            {(open) && (
                <Callout
                    style={{
                        padding: '1em',
                    }}
                    directionalHint={DirectionalHint.leftCenter}
                    target={`#${id}`}
                    onDismiss={() => setOpen(false)}
                    role='status'
                >
                    {props.refreshing ? <p>Loading</p> :
                    <WarehouseTable vendors={props.vendors} product={props.product}/>}
                    <AddToCollectionButton 
                        product={props.product} 
                        source={props.vendorName} 
                        onChange={() => setOpen(false)}
                        isExample={props.isDemo}
                    />
                    {props.priceBreaks != null && props.priceBreaks.some(pb => pb.quantity > 1) && (
                        <PriceBreaksTable
                            baseCost={props.lowInStockSingleCost ?? props.lowSingleCost ?? props.highCost}
                            priceBreaks={props.priceBreaks}
                        />
                    )}
                </Callout>
            )}
        </div>
    }
    const baseCost = props.lowInStockSingleCost ?? props.lowSingleCost ?? props.highCost;
    return <div
        id={id}
        onClick={() => setOpen(!open)}
        className='figure-cell'
    >
        {stockLine}
        <span className='figure-main'>{baseCost != null ? <CurrencyDisplay value={baseCost} /> : 'N/A'}</span>
        <span className='figure-bottom' style={{fontStyle: 'normal'}}>
            <span style={{fontWeight: 600}}>
                Price Breaks
                <span style={{paddingLeft: '0.5em'}}>
                    <TooltipHost
                        content={'The price breaks may start at lower quantities. Please check with your distributor for exact price breaks.'}>
                        <Icon iconName={'alertSolid'}/>
                    </TooltipHost>
                </span>
            </span>
            <PriceBreaksTable baseCost={baseCost} priceBreaks={props.priceBreaks ?? []} list/>
            {customerPricingTag}
        </span>
        {(open) && (
            <Callout
                style={{
                    padding: '1em',
                }}
                directionalHint={DirectionalHint.leftCenter}
                target={`#${id}`}
                onDismiss={() => setOpen(false)}
                role='status'
            >
                {props.refreshing ? <p>Loading</p> :
                <WarehouseTable vendors={props.vendors} product={props.product}/>}
                <AddToCollectionButton 
                    product={props.product} 
                    source={props.vendorName} 
                    onChange={() => setOpen(false)}
                    isExample={props.isDemo}
                />
            </Callout>
        )}
    </div>;
}


export default VendorDisplay;
